import { Navigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { LoadingState } from '@bcecloud/portal-ui';
import { APP_FEATURES_QUERY_KEY, readAppFeatures, type AppFeatureKey } from '@/lib/app-features';

export interface RequireFeatureProps {
  readonly feature: AppFeatureKey;
  readonly children: React.ReactNode;
}

/**
 * Route guard for a switchable feature (Settings → Features): renders the
 * page while the feature is on, and sends a bookmarked or typed URL back to
 * the events home once an admin has turned it off. A failed read counts as
 * on, so an outage never locks anyone out of a screen.
 *
 * @param props - The feature and the guarded page.
 * @returns The page, a loader, or a redirect.
 */
export function RequireFeature({ feature, children }: RequireFeatureProps): React.ReactElement {
  const features = useQuery({
    queryKey: APP_FEATURES_QUERY_KEY,
    queryFn: ({ signal }) => readAppFeatures(signal),
    staleTime: 5 * 60_000,
  });

  if (features.isPending) return <LoadingState label="Loading…" />;
  if (features.data?.enabled[feature] === false) return <Navigate to="/events" replace />;
  return <>{children}</>;
}
